// Medical & Mobility section content. One source of truth for the hub, the five category pages,
// the dedicated navigation and the SEO metadata.
//
// Imagery: the approved lifestyle images supplied by the owner, published as pre-generated
// WebP derivatives under /media/medical-mobility. `base` is the filename without its width suffix.
//
// Partners: `offerCategory` is the offer category an approved partner is filed under in the
// existing admin (Offers). Category pages read /api/offers/group, which only ever returns active,
// approved offers that carry a tracking URL — so nothing appears here until a partnership is real.

export const MM_ROOT = "/medical-mobility";

// Offer categories featured on the landing page, in display order.
export const MM_FEATURED_CATEGORIES =
  "power-mobility,medical-beds,home-care-support,respiratory-emergency";

export const MM_BRAND = {
  name: "Human Home Living",
  division: "Medical & Mobility",
  tagline: "Premium Equipment for Comfort, Care & Independence",
};

export const MM_HERO_IMAGE = {
  base: "/media/medical-mobility/home-elevators-accessibility",
  alt:
    "A woman stepping out of a residential elevator into the bright open living space of an " +
    "upscale home, with a stair lift on the staircase behind her",
};

export const MM_INTRO =
  "Discover premium medical, mobility, and accessibility equipment designed to support comfort, " +
  "independence, and better everyday living. Explore thoughtfully organized equipment categories " +
  "for your home and lifestyle.";

// Short, scannable reasons this decision deserves care. Rendered on the hub below the grid.
export const MM_WHY = {
  eyebrow: "Why the right equipment matters",
  heading: "Equipment that fits the home, not just the need",
  body:
    "Medical and mobility equipment lives in a home for years. The difference between a good " +
    "decision and a frustrating one is usually not the product itself — it is how well that " +
    "product suits the doorways, the floor plan, the power supply, the people using it and the " +
    "way the household actually lives.",
  points: [
    {
      h: "Comfort and daily convenience",
      p:
        "Equipment used every day should be pleasant to use. Seat width, transfer height, control " +
        "layout and noise matter far more in month six than they do in the showroom.",
    },
    {
      h: "Independence at home",
      p:
        "The best outcome is usually the one that lets someone do more for themselves. Look for " +
        "equipment that removes a specific barrier rather than adding supervision.",
    },
    {
      h: "Space, access and installation",
      p:
        "Measure before you shop. Doorway widths, turning space, landing size, ceiling structure " +
        "and electrical capacity decide which options are genuinely available to you.",
    },
    {
      h: "Long-term usability",
      p:
        "Needs change. Adjustable, modular and serviceable equipment tends to stay useful longer " +
        "than equipment built for one precise set of circumstances.",
    },
    {
      h: "Who will be using it",
      p:
        "Weight capacity, reach, grip strength, cognitive load and caregiver involvement all shape " +
        "which category of equipment is appropriate in the first place.",
    },
    {
      h: "Service, warranty and support",
      p:
        "For high-value equipment, ask who installs it, who services it, how quickly parts arrive " +
        "and what the warranty actually covers before you compare prices.",
    },
  ],
};

export const MM_ABOUT = {
  eyebrow: "About this section",
  heading: "A specialized part of Human Home Living",
  body: [
    "Medical & Mobility is a dedicated part of Human Home Living focused on helping people " +
      "explore premium home medical, mobility and accessibility equipment with clear, practical " +
      "information.",
    "Human Home Living is a homeowner decision platform, not a healthcare provider, equipment " +
      "manufacturer or medical professional. We publish research and comparison information to " +
      "help people understand their options, and we may earn a commission if a visitor purchases " +
      "through a partner link once partnerships are in place.",
  ],
};

export const MM_DISCLAIMER =
  "Information in this section is provided for general research and planning purposes only and is " +
  "not medical advice. Human Home Living is not a healthcare provider or medical equipment " +
  "manufacturer. Depending on the product, medical and mobility equipment may require " +
  "professional assessment, measurement, training, installation or a prescription. Always confirm " +
  "suitability with a qualified clinician, therapist or installer for your situation.";

export const MM_AFFILIATE_NOTE =
  "Human Home Living may earn a commission when partner links appear on this page and a purchase " +
  "is made, at no additional cost to you. We do not list partners or products until a partnership " +
  "is approved.";

export const MM_CATEGORIES = [
  {
    slug: "home-accessibility",
    name: "Home Elevators & Accessibility",
    nav: "Home Elevators & Accessibility",
    eyebrow: "Category 01",
    cardText:
      "Residential elevators, stair lifts and platform lifts that make every floor of the home " +
      "reachable again.",
    image: {
      base: "/media/medical-mobility/home-elevators-accessibility",
      alt:
        "A woman stepping out of a residential elevator into a bright open living space, with a " +
        "stair lift on the staircase behind her",
    },
    offerCategory: "medical-home-accessibility",
    subPages: ["stair-lifts", "home-elevators"],
    seo: {
      title: "Home Elevators & Accessibility Equipment — Human Home Living Medical & Mobility",
      description:
        "Compare residential elevators, stair lifts, vertical platform lifts and home " +
        "accessibility systems, including space, installation and home compatibility " +
        "considerations.",
    },
    lede:
      "When stairs become the limiting factor in a home, accessibility equipment is often what " +
      "keeps the whole house usable. Because these systems often require professional " +
      "installation, it's important to understand your home's layout and requirements before " +
      "choosing one.",
    // Approved in-page imagery, published as pre-generated WebP alongside the category images.
    sectionImage: {
      base: "/media/medical-mobility/residential-elevator-glass-cab",
      alt:
        "Custom glass residential elevator installed between floors in a luxury modern home.",
      width: 1536,
      height: 1024,
      widths: [480, 768, 1024, 1536],
    },
    overview: [
      "Stairs and changes in floor level can make parts of a home difficult to access. The right " +
        "equipment can help make everyday movement easier. The right answer depends less on the " +
        "person and more on the building — how much floor area can be given up, what sits above " +
        "and below the proposed shaft, how the staircase turns, and how much electrical capacity " +
        "is available.",
      "Because these products are installed rather than delivered, they are usually quoted after " +
        "a site assessment. Treat published figures as a starting range and expect the final " +
        "specification to be shaped by your home.",
    ],
    equipment: [
      {
        h: "Residential elevators",
        p:
          "Fully enclosed cabs serving two or more floors. Drive types differ in footprint, pit " +
          "and headroom requirements, ride quality and machine-room needs, which is usually what " +
          "narrows the choice for a given home.",
      },
      {
        h: "Stair lifts",
        p:
          "A powered seat on a rail fixed to the stair treads. Straight rails are the simplest " +
          "and fastest to fit; curved and multi-landing staircases require a rail manufactured to " +
          "measure.",
      },
      {
        h: "Vertical platform lifts",
        p:
          "Open or enclosed platforms that carry a wheelchair or scooter through a short rise — " +
          "typically a porch, split-level or garage entry — without a full elevator installation.",
      },
      {
        h: "Inclined platform lifts",
        p:
          "A platform that travels along a staircase rather than through a shaft, used where a " +
          "wheelchair must stay with its user and a vertical lift will not fit.",
      },
      {
        h: "Home accessibility systems",
        p:
          "Modular and threshold ramps, widened entries, grab systems and level-access " +
          "alterations that remove smaller barriers around the lift or stair solution.",
      },
    ],
    considerations: [
      {
        h: "Space and structure",
        p:
          "Elevators need a shaft, overhead clearance and, in most cases, a pit. Stair lifts need " +
          "clear stair width with the carriage parked. Confirm the actual dimensions on site " +
          "before shortlisting.",
      },
      {
        h: "Installation and permits",
        p:
          "Most of these products are professionally installed and many require permits and " +
          "inspection. Ask what the quote includes: framing, electrical work, finishing and " +
          "inspection fees are often separate.",
      },
      {
        h: "Power supply",
        p:
          "Dedicated circuits are common. Some units offer battery backup so the lift still works " +
          "during an outage — worth asking about if a bedroom is upstairs.",
      },
      {
        h: "Professional assessment",
        p:
          "An occupational therapist or accessibility specialist can help determine which " +
          "equipment best fits your home and daily needs.",
      },
      {
        h: "Service and maintenance",
        p:
          "These are mechanical systems with scheduled servicing. Confirm response times, parts " +
          "availability and the cost of a service plan before comparing purchase prices.",
      },
    ],
  },

  {
    slug: "stair-lifts",
    name: "Stair Lifts & Platform Lifts",
    nav: "Stair Lifts",
    eyebrow: "Guide",
    hideFromGrid: true,
    informational: true,
    cardText:
      "Straight, curved and outdoor stair lifts, plus wheelchair platform lifts — what fits which " +
      "staircase, and what it costs.",
    image: {
      base: "/media/medical-mobility/home-elevators-accessibility",
      alt:
        "A staircase fitted with a stair lift in a bright home, beside an open living space",
    },
    seo: {
      title: "Stair Lifts & Platform Lifts: Buyer's Guide — Human Home Living Medical & Mobility",
      description:
        "An independent guide to straight, curved and outdoor stair lifts and wheelchair platform " +
        "lifts — staircase measurements, installation, weight capacity, battery backup, warranty " +
        "and published 2026 cost ranges.",
    },
    lede:
      "A stair lift is chosen by the staircase, not by the budget. Whether your stairs run in one " +
      "straight line or turn at a landing decides which products exist for your home, how long " +
      "they take to deliver and most of what you will pay.",
    overview: [
      "Walk the staircase before you call anyone and answer one question: does it run in a single " +
        "uninterrupted line, with no turn, bend or intermediate landing? A straight staircase " +
        "takes a standard rail cut to length. Any turn or landing requires a rail fabricated for " +
        "your stairs specifically, which is the single biggest cost difference in this category.",
      "Measure the clear width of the stairs at their narrowest point as well. That one figure " +
        "rules products in or out immediately, and every dealer will ask for it. A written " +
        "in-home quote after a site survey is the only price that is real — any firm number " +
        "quoted before your staircase has been measured will be revised later.",
    ],
    equipment: [
      {
        h: "Straight stair lifts",
        p:
          "For a single flight with no turns. The rail is a standard product cut to length and " +
          "fixed to the stair treads rather than the wall, so most staircases can take one. Lead " +
          "times are typically days to about a week.",
      },
      {
        h: "Curved stair lifts",
        p:
          "Required — not merely preferred — the moment there is a bend, winder or intermediate " +
          "landing. The rail is measured or digitally surveyed in your home and manufactured to " +
          "that geometry, which commonly adds two to six weeks before installation.",
      },
      {
        h: "Outdoor stair lifts",
        p:
          "Built for exterior steps, decks and garage entries with weather-resistant materials, " +
          "sealed rail mechanisms and UV-resistant upholstery. Relevant when the outdoor steps " +
          "are the barrier to getting into the house at all.",
      },
      {
        h: "Standing or perch lifts",
        p:
          "A padded support instead of a full seat, for riders who cannot bend comfortably at the " +
          "hip or knee. The rider must be able to bear weight through their legs and stay " +
          "balanced for the whole ride, so a therapist's assessment belongs in this decision.",
      },
      {
        h: "Wheelchair platform lifts (vertical)",
        p:
          "An open or enclosed platform carrying a wheelchair or scooter through a short rise — a " +
          "porch, split level or garage entry — without the construction a full elevator needs.",
      },
      {
        h: "Inclined platform lifts",
        p:
          "A platform that travels along the staircase itself, used where the rider must stay in " +
          "their wheelchair and a vertical lift or elevator will not fit the space.",
      },
    ],
    considerations: [
      {
        h: "Staircase configuration and measurements",
        p:
          "Straight or turning is the first question. Then measure the clear stair width at its " +
          "narrowest point in at least two places and use the smaller number, and check the stair " +
          "pitch, tread condition and headroom at the top.",
      },
      {
        h: "Installation requirements",
        p:
          "Most lifts plug into a standard household outlet, but it has to be within reach of the " +
          "rail. If it is not, an electrician runs one before installation day — ask whether that " +
          "work is inside or outside the quote.",
      },
      {
        h: "Weight capacity and accessibility",
        p:
          "Standard seats are commonly rated around 250–300 lb, with heavier-duty models " +
          "available. Match the rating to the rider's actual weight, and remember a stair lift " +
          "only works for someone who can transfer onto and off the seat.",
      },
      {
        h: "Battery backup and safety features",
        p:
          "Most modern lifts run on batteries that charge at a parking position, so they still " +
          "work in an outage if the lift is parked correctly. Obstruction sensors on the footrest " +
          "and rail stop the lift; ask for the manual-lowering procedure to be demonstrated.",
      },
      {
        h: "Warranty and maintenance",
        p:
          "Ask what the warranty covers — the unit only, or the installation labour too — how " +
          "fast a service technician can attend, and whether removal at end of life is included " +
          "or charged separately. Test the obstruction sensors yearly.",
      },
      {
        h: "Direct purchase versus custom installation",
        p:
          "A small number of straight-rail units are sold online for self-installation; curved " +
          "and platform lifts are surveyed, fabricated and installed by a dealer. If a unit is " +
          "advertised as DIY, confirm with the manufacturer what self-installation does to the " +
          "warranty before buying.",
      },
    ],
    costs: {
      intro:
        "Published ranges differ by thousands of dollars because rail length, staircase geometry, " +
        "electrical work and the unit itself all move the number. Use these as planning ranges " +
        "only. Figures are quoted as published by each source on the date shown.",
      includes:
        "A professionally installed price should cover the lift, the rail, delivery, " +
        "installation and initial setup. Electrical work, a powered swivel seat or folding " +
        "footrest, extra rail length, higher weight capacities and outdoor specification are " +
        "commonly priced on top. Rental, where offered, applies almost exclusively to straight " +
        "units.",
      rows: [
        {
          label: "Straight stair lift",
          equipment: "Rail and seat are a standard product",
          installed: "$3,300 – $5,000 installed",
          source: "Savaria, 2026",
        },
        {
          label: "Straight stair lift (range across guides)",
          equipment: "Varies with rail length and local labour",
          installed: "$2,000 – $8,500 installed",
          source: "Graying With Grace, 2026",
        },
        {
          label: "Curved stair lift",
          equipment: "Rail custom-fabricated for your staircase",
          installed: "$10,000 – $25,000 installed",
          source: "Savaria, 2026",
        },
        {
          label: "Curved stair lift (range across guides)",
          equipment: "More turns and landings push it higher",
          installed: "$10,000 – $20,000 installed",
          source: "Graying With Grace, 2026",
        },
        {
          label: "Stair lifts generally",
          equipment: "Across straight and curved models",
          installed: "$2,900 – $14,000",
          source: "Retirement Living, updated 23 September 2026",
        },
        {
          label: "Straight stair lift rental",
          equipment: "Deposit $500–$1,000; removal $300–$600",
          installed: "$100 – $500 per month, plus $400–$700 installation",
          source: "Graying With Grace, 2026",
        },
      ],
      sources: [
        {
          name: "Savaria",
          title: "How Much Does a Stairlift Cost in 2026?",
          dated: "2026",
          url: "https://www.savaria.com/news/how-much-does-a-stairlift-cost-in-2026",
        },
        {
          name: "Graying With Grace",
          title: "Stairlifts: Costs, Types, and When the Stairs Win",
          dated: "2026",
          url: "https://www.grayingwithgrace.com/stairlift-costs-types-guide/",
        },
        {
          name: "Retirement Living",
          title: "How Much Does a Home Elevator Cost? (stair lift comparison section)",
          dated: "updated 23 September 2026",
          url: "https://www.retirementliving.com/home-elevators/how-much-does-a-home-elevator-cost",
        },
      ],
      coverageNote:
        "Original Medicare generally does not cover stair lifts, which are usually treated as a " +
        "home modification rather than covered durable medical equipment. Some Medicare " +
        "Advantage, Medicaid waiver, state, veterans' or long-term care insurance programmes may " +
        "help in particular circumstances. Confirm eligibility with your own plan or programme " +
        "before you buy — nothing here is a guarantee of coverage.",
    },
    questions: [
      "Is my staircase straight, or does it have a turn, winder or intermediate landing?",
      "What is the clear stair width at its narrowest point, and will the folded lift still leave " +
        "the stairs usable for everyone else?",
      "Does the written quote itemise the unit, the rail, installation labour, any electrical " +
        "work and removal at end of life?",
      "Is there a power outlet within reach of the rail, and who pays if one has to be run?",
      "What is the rated weight capacity, and how does it compare with the rider's actual weight?",
      "Can the rider transfer on and off the seat unaided, or will someone need to be present?",
      "What does the warranty cover — the unit, or the installation as well — and for how long?",
      "How quickly can a service technician attend, and are parts held locally?",
      "How is the lift lowered manually during a power cut, and who in the house will be shown how?",
      "For a curved rail, how long is fabrication, and what is the plan for the weeks until it is " +
        "installed?",
    ],
    partnership: "short",
    informationalNote:
      "This page is editorial research, not a shop. Human Home Living has no affiliate or " +
      "referral relationship with any stair lift manufacturer, dealer or installer, so there is " +
      "nothing here to click through and nothing we earn from the choice you make. Brands are " +
      "named only where it is useful for comparison.",
  },

  {
    slug: "home-elevators",
    name: "In-Home Elevators",
    nav: "In-Home Elevators",
    eyebrow: "Guide",
    hideFromGrid: true,
    informational: true,
    cardText:
      "Shaftless, pneumatic, hydraulic and traction systems — space, electrical, permits, " +
      "maintenance and published cost ranges.",
    image: {
      base: "/media/medical-mobility/residential-elevator-glass-cab",
      alt:
        "A custom glass residential elevator installed between floors in a modern home",
      width: 1536,
      height: 1024,
      widths: [480, 768, 1024, 1536],
    },
    seo: {
      title: "In-Home Elevators: Planning & Cost Guide — Human Home Living Medical & Mobility",
      description:
        "An independent guide to residential elevators — shaftless, pneumatic, hydraulic and " +
        "traction systems, space and structural requirements, electrical supply, permits, " +
        "maintenance and published 2026 cost ranges.",
    },
    lede:
      "A residential elevator is a construction project as much as a product. Almost every " +
      "decision — type, size, where it can go — is settled by the building rather than by " +
      "preference, which is why these systems are quoted after a professional site evaluation " +
      "rather than priced from a page.",
    overview: [
      "The practical starting point is where a shaft or lift path could physically go on every " +
        "floor it has to serve, what sits above and below it, and how many stops you need. From " +
        "there the drive type narrows quickly: each one differs in footprint, pit and headroom " +
        "requirements, whether a machine space is needed, and how the ride feels.",
      "Expect the quotation to arrive after a site evaluation, and expect it to be itemised: the " +
        "elevator itself is only part of the project once framing, electrical work, doors, " +
        "finishes, permits and inspection are included. Retrofitting an existing house normally " +
        "costs more than planning a lift into new construction.",
    ],
    equipment: [
      {
        h: "Traditional elevators with a hoistway",
        p:
          "A fully enclosed cab running in a constructed shaft, serving two or more floors with " +
          "automatic operation. This is the most house-like installation and the one that " +
          "involves the most building work.",
      },
      {
        h: "Shaftless and through-floor lifts",
        p:
          "A compact cab that travels through an opening cut between two floors, with far less " +
          "site preparation than a hoistway. Usually the entry point into the category, limited " +
          "to two stops and a smaller cab.",
      },
      {
        h: "Pneumatic (vacuum) elevators",
        p:
          "A cylindrical cab moved by air pressure inside a self-supporting tube. No pit, " +
          "hoistway or machine room is normally required, which is why they are often proposed " +
          "for retrofits, with capacity and cab size the trade-off.",
      },
      {
        h: "Hydraulic systems",
        p:
          "A piston-driven cab, typically with a machine space for the pump and tank. Common in " +
          "residential installations and generally tolerant of heavier loads, but it asks for " +
          "more space around the shaft.",
      },
      {
        h: "Traction and drum-driven systems",
        p:
          "Cable-driven cabs, including counterweighted traction and winding-drum designs, plus " +
          "machine-room-less variants. Ride quality and efficiency are the usual arguments for " +
          "them; equipment cost and shaft requirements are the usual arguments against.",
      },
      {
        h: "Planning a residential elevator",
        p:
          "Stacked closets, an unused corner of a stairwell and new-build framing are the common " +
          "ways a lift gets a home. An architect, builder or elevator contractor should confirm " +
          "the location before anything is ordered.",
      },
    ],
    considerations: [
      {
        h: "Space and structural requirements",
        p:
          "Confirm the shaft or lift footprint on every floor it serves, plus overhead clearance " +
          "and, for most systems, a pit. What sits directly above and below the proposed path " +
          "often decides the project before any model is chosen.",
      },
      {
        h: "Electrical requirements",
        p:
          "Residential elevators generally need dedicated circuits — commonly a dedicated supply " +
          "for the drive and a separate one for cab lighting. Have an electrician confirm what " +
          "your panel can support as part of the quotation, not afterwards.",
      },
      {
        h: "Permits and safety standards",
        p:
          "These installations are normally permitted and inspected, and are governed by " +
          "elevator safety codes that vary by jurisdiction. Ask which permits the contractor " +
          "pulls, which inspections apply locally and whether those fees are in the quote.",
      },
      {
        h: "Professional installation",
        p:
          "Installation is specialist work, not general contracting. Ask who performs it, who is " +
          "licensed for elevator work in your area, and how long the house is disrupted.",
      },
      {
        h: "Maintenance and servicing",
        p:
          "Plan for an annual service contract and keep the work with a certified technician so " +
          "the warranty stays intact. Published annual maintenance figures are included in the " +
          "cost table below.",
      },
      {
        h: "Battery backup and emergency features",
        p:
          "Ask about battery lowering during an outage, emergency communication inside the cab, " +
          "door interlocks and safety sensors. For a household where the only bedroom is " +
          "upstairs, outage behaviour is not a detail.",
      },
    ],
    costs: {
      intro:
        "Residential elevators are priced per project after a site evaluation. The ranges below " +
        "are published planning figures, quoted as each source states them on the date shown — " +
        "they are not quotes and should not be read as one.",
      includes:
        "Sources differ on what their figures include. Savaria's ranges are installed prices for " +
        "the elevator and dealer installation. Retirement Living reports equipment ranges " +
        "separately from labour, which it puts at $5,000–$15,000, and notes that hoistway " +
        "construction, machine rooms, cab finishes, permits and local labour rates move the total " +
        "well beyond the equipment price.",
      rows: [
        {
          label: "Shaftless / through-floor lift, 2 stops",
          equipment: "Simplest site preparation",
          installed: "$30,000 – $40,000 installed",
          source: "Savaria, 2026",
        },
        {
          label: "Traditional elevator with hoistway, 2–3 stops",
          equipment: "Constructed hoistway required",
          installed: "$45,000 – $75,000 installed",
          source: "Savaria, 2026",
        },
        {
          label: "3–5 stops with luxury finishes",
          equipment: "Custom cab and upgrades",
          installed: "$75,000+ installed",
          source: "Savaria, 2026",
        },
        {
          label: "Shaftless / through-the-floor",
          equipment: "$35,000 – $60,000 equipment",
          installed: "Labour $5,000 – $15,000 on top",
          source: "Retirement Living, updated 23 September 2026",
        },
        {
          label: "Pneumatic / vacuum",
          equipment: "$35,000 – $85,000 equipment",
          installed: "Labour $5,000 – $15,000 on top",
          source: "Retirement Living, updated 23 September 2026",
        },
        {
          label: "Hydraulic or traction with hoistway",
          equipment: "$45,000 – $85,000+ equipment",
          installed: "Labour $5,000 – $15,000 on top",
          source: "Retirement Living, updated 23 September 2026",
        },
        {
          label: "Annual maintenance",
          equipment: "Varies with contract and usage",
          installed: "$800 – $1,400 per year",
          source: "Retirement Living, updated 23 September 2026",
        },
      ],
      sources: [
        {
          name: "Savaria",
          title: "How Much Does a Home Elevator Cost in 2026?",
          dated: "2026",
          url: "https://www.savaria.com/news/how-much-does-a-home-elevator-cost",
        },
        {
          name: "Retirement Living",
          title: "How Much Does a Home Elevator Cost?",
          dated: "updated 23 September 2026",
          url: "https://www.retirementliving.com/home-elevators/how-much-does-a-home-elevator-cost",
        },
      ],
      coverageNote:
        "Home elevators are generally not covered by Medicare, and most insurance plans do not " +
        "cover installation. Depending on your circumstances, tax deductions, veterans' " +
        "programmes, state assistance or a letter of medical necessity used with an FSA, HSA or " +
        "HRA may apply. Verify any of this with the programme itself before relying on it.",
    },
    questions: [
      "Where could the lift physically go on every floor it needs to serve, and what sits above " +
        "and below that path?",
      "How many stops do we actually need now, and will that change?",
      "Which drive type suits this building, and what pit, headroom and machine space does it need?",
      "Is the quote itemised — elevator, hoistway framing, electrical, doors, finishes, permits " +
        "and inspection?",
      "What electrical supply is required, and can our existing panel support it?",
      "Which permits and inspections apply locally, and who arranges them?",
      "Who installs it, are they licensed for elevator work here, and how long is the house " +
        "disrupted?",
      "What does the lift do during a power cut, and is there emergency communication in the cab?",
      "What does the annual service contract include, and what are the hourly rates outside it?",
      "What is the warranty, and what would void it?",
    ],
    partnership: "short",
    informationalNote:
      "This page is editorial research, not a shop. Human Home Living has no affiliate or " +
      "referral relationship with any elevator manufacturer, dealer or installer — there are no " +
      "shopping buttons here and we earn nothing from what you decide. Manufacturers are named " +
      "only as sources or for factual comparison.",
  },

  {
    slug: "medical-beds",
    name: "Premium Medical Beds",
    nav: "Premium Medical Beds",
    eyebrow: "Category 02",
    cardText:
      "Adjustable home-care beds that look like bedroom furniture and work like clinical " +
      "equipment.",
    image: {
      base: "/media/medical-mobility/premium-medical-beds",
      alt:
        "A woman reading comfortably in an adjustable medical bed in an elegant bedroom with a " +
        "lake view",
    },
    offerCategory: "medical-beds",
    seo: {
      title: "Premium Medical Beds for the Home — Human Home Living Medical & Mobility",
      description:
        "Compare premium adjustable medical beds, full-electric home-care beds and rotating care " +
        "beds, with guidance on positioning, comfort, dimensions and home suitability.",
    },
    lede:
      "A home-care bed has to do two jobs at once: support safe positioning and transfers, and " +
      "still belong in a bedroom someone wants to spend time in. The premium end of this category " +
      "is largely about achieving both.",
    overview: [
      "Medical beds are defined by how they move. Head and knee articulation, overall height " +
        "adjustment, tilt functions and side-rail configuration determine what is possible for " +
        "both the person in the bed and anyone helping them.",
      "Height range is the detail most often underestimated. A bed that lowers far enough reduces " +
        "fall risk; a bed that raises far enough protects a caregiver's back. Few homes need the " +
        "widest feature set, but almost every home benefits from the right height range.",
    ],
    equipment: [
      {
        h: "Premium adjustable beds",
        p:
          "Home-styled frames with clinical articulation, upholstered surrounds and furniture " +
          "finishes, designed to sit in a normal bedroom rather than announce itself.",
      },
      {
        h: "Full-electric home-care beds",
        p:
          "Head, foot and height all powered from a handset, so positioning does not depend on " +
          "someone being strong enough to crank it.",
      },
      {
        h: "Rotating care beds",
        p:
          "Frames that rotate the sleeping surface towards the side of the bed, turning a lift " +
          "and pivot into a seated edge-of-bed position for easier transfers.",
      },
      {
        h: "Advanced positioning beds",
        p:
          "Beds offering tilt positions, lateral turn assistance or programmable presets, " +
          "typically specified on clinical advice for pressure care or respiratory comfort.",
      },
      {
        h: "Luxury home-care sleeping systems",
        p:
          "Complete setups — frame, therapeutic mattress, rails and accessories — matched so the " +
          "mattress does not undo the frame's articulation.",
      },
    ],
    considerations: [
      {
        h: "Adjustability you will actually use",
        p:
          "List the three positions that matter most — sitting up, transferring out, sleeping " +
          "comfortably — and check each one, rather than counting features.",
      },
      {
        h: "Height range",
        p:
          "Check the lowest and highest deck heights with the intended mattress fitted. This is " +
          "the number that governs both fall risk and caregiver strain.",
      },
      {
        h: "Dimensions and access",
        p:
          "Confirm the bed will pass through doorways and up stairs in sections, and that there " +
          "is working space on at least one side for transfers or equipment.",
      },
      {
        h: "Mattress compatibility",
        p:
          "Therapeutic and air mattresses change the deck height and sometimes need their own " +
          "power supply. Specify the mattress with the bed, not after it.",
      },
      {
        h: "Safety and clinical guidance",
        p:
          "Side rails, restraint use and pressure management should follow clinical advice. A " +
          "therapist's recommendation often determines which of these beds is appropriate.",
      },
    ],
  },

  {
    slug: "power-mobility",
    name: "Power Mobility",
    nav: "Power Mobility",
    eyebrow: "Category 03",
    cardText:
      "Power wheelchairs, premium scooters and folding electric mobility for getting out and " +
      "staying out.",
    image: {
      base: "/media/medical-mobility/power-mobility",
      alt:
        "A woman driving a premium four-wheel mobility scooter along the paved entrance of a " +
        "beautiful home on a sunny day",
    },
    offerCategory: "power-mobility",
    seo: {
      title: "Power Wheelchairs & Premium Mobility Scooters — Human Home Living Medical & Mobility",
      description:
        "Compare premium power wheelchairs, high-end mobility scooters and folding electric " +
        "mobility equipment, including range, maneuverability, charging and transport " +
        "considerations.",
    },
    lede:
      "Power mobility is bought for range of life, not range in miles. The practical question is " +
      "where the equipment needs to go — indoors, around the neighbourhood, into a vehicle — " +
      "because that usually rules out whole classes of product.",
    overview: [
      "Indoor use rewards a tight turning radius and a compact base. Outdoor use rewards larger " +
        "wheels, suspension and battery capacity. Equipment that travels frequently rewards " +
        "weight and folded size. Very few products are excellent at all three.",
      "Drive-wheel position is the other decision that shapes everyday use: front, mid and rear " +
        "wheel drive each handle turning, kerbs and uneven ground differently, which is why a " +
        "test drive on the surfaces you actually use is worth more than a specification sheet.",
    ],
    equipment: [
      {
        h: "Premium power wheelchairs",
        p:
          "Powered bases with clinical seating, often including tilt, recline, powered legrests " +
          "or seat elevation for posture, pressure relief and reach.",
      },
      {
        h: "High-end mobility scooters",
        p:
          "Three and four-wheel scooters for people who can walk short distances and transfer " +
          "independently. Four wheels favour stability, three favour turning space.",
      },
      {
        h: "Folding electric mobility",
        p:
          "Lightweight chairs and scooters that fold or separate for a car boot, trading range " +
          "and ground clearance for portability.",
      },
      {
        h: "Specialty mobility solutions",
        p:
          "All-terrain, standing, bariatric and custom-configured equipment for needs the " +
          "mainstream ranges do not cover.",
      },
      {
        h: "Manual wheelchairs",
        p:
          "Self-propelled and attendant-pushed chairs, from transit chairs kept for appointments " +
          "to lightweight rigid frames configured for full-time use. Seat width, back height and " +
          "rear-wheel position do more for comfort and pushing effort than the headline weight.",
      },
      {
        h: "Walkers and rollators",
        p:
          "Standard walkers give the most support and the least speed; rollators with wheels, " +
          "brakes and a seat suit longer distances and anyone who needs to rest en route. Check " +
          "the folded width against your narrowest doorway and the handle height against the " +
          "user's wrist crease.",
      },
      {
        h: "Daily living mobility aids",
        p:
          "Canes, crutches, transfer boards, grab rails, bed rails and reachers — the small " +
          "items that decide whether the larger equipment gets used safely. Many are specified " +
          "by a therapist rather than chosen from a catalogue.",
      },
    ],
    considerations: [
      {
        h: "Maneuverability",
        p:
          "Compare turning radius against your tightest real doorway, hallway and bathroom — not " +
          "against an average room.",
      },
      {
        h: "Range and terrain",
        p:
          "Published range assumes ideal conditions. Hills, cold weather, rider weight and soft " +
          "ground all reduce it, so build in margin for the return trip.",
      },
      {
        h: "Charging and batteries",
        p:
          "Check charge time, whether batteries can be removed and charged indoors, and the " +
          "expected replacement interval and cost.",
      },
      {
        h: "Dimensions and weight",
        p:
          "Note overall width, length, ground clearance and total weight — including the heaviest " +
          "single piece if someone has to lift it.",
      },
      {
        h: "Transportation",
        p:
          "Confirm how the equipment travels: folded into a boot, on a vehicle lift, or secured " +
          "inside an accessible vehicle with the user seated.",
      },
      {
        h: "Professional fitting",
        p:
          "Seating, posture and controls for a power wheelchair are usually specified with a " +
          "clinician. Scooters are simpler, but transfers and balance still need an honest " +
          "assessment.",
      },
    ],
  },

  {
    slug: "home-care",
    name: "Home Care & Patient Support",
    nav: "Home Care & Patient Support",
    eyebrow: "Category 04",
    cardText:
      "Transfer lifts, accessible bathing and support equipment that make care at home safer for " +
      "everyone.",
    image: {
      base: "/media/medical-mobility/home-care-patient-support",
      alt:
        "A caregiver assisting a woman using a home patient-transfer lift in a bright bedroom " +
        "beside an accessible bathroom",
    },
    offerCategory: "home-care-support",
    seo: {
      title: "Patient Lifts & Home Care Support Equipment — Human Home Living Medical & Mobility",
      description:
        "Compare patient transfer lifts, accessible bathing equipment and home care support " +
        "systems, with guidance on fit, space, caregiver needs and safe operation.",
    },
    lede:
      "This category exists to protect two people at once. Equipment that makes a transfer safer " +
      "for the person being moved usually also protects the person doing the moving — which is " +
      "what keeps care at home sustainable.",
    overview: [
      "Transfers are where most home-care injuries happen, for both parties. Choosing equipment " +
        "starts with how much the person can contribute to the movement: equipment that assists a " +
        "standing transfer is very different from equipment that lifts someone entirely.",
      "Bathing is the other high-risk routine. Wet surfaces, confined space and awkward " +
        "clearances make this the room where good equipment and a professional assessment make " +
        "the biggest difference.",
    ],
    equipment: [
      {
        h: "Patient transfer lifts",
        p:
          "Mobile floor lifts, sit-to-stand lifts and ceiling track systems. Floor lifts need " +
          "clear floor space and a base that fits under the bed; ceiling systems need structural " +
          "fixing but almost no floor space.",
      },
      {
        h: "Transfer systems",
        p:
          "Slide sheets, transfer boards, turntables and pivot aids for shorter moves where a " +
          "full lift is not required.",
      },
      {
        h: "Accessible bathing equipment",
        p:
          "Walk-in and transfer-accessible bathing, shower chairs and commodes, bath lifts and " +
          "grab systems positioned for the way the room is actually used.",
      },
      {
        h: "Home-care support equipment",
        p:
          "Overbed tables, bed rails and supports, positioning cushions and pressure-management " +
          "accessories that make longer periods in bed or a chair more comfortable.",
      },
      {
        h: "Supportive home-care solutions",
        p:
          "Monitoring, call systems and smaller daily-living aids that reduce how often help has " +
          "to be summoned in the first place.",
      },
    ],
    considerations: [
      {
        h: "Equipment fit",
        p:
          "Slings, seats and supports are sized to a person. The wrong size is unsafe regardless " +
          "of how good the lift is, so size them with a professional.",
      },
      {
        h: "Space requirements",
        p:
          "Measure the working space around the bed, chair and bathroom. A mobile lift needs room " +
          "to approach, spread its legs and turn with someone in the sling.",
      },
      {
        h: "Caregiver needs",
        p:
          "Consider who performs transfers, how often, and alone or in pairs. Powered equipment " +
          "that one person can operate safely often matters more than top specification.",
      },
      {
        h: "Safe operation and training",
        p:
          "Lifts and slings require training. Confirm who provides it, and that everyone " +
          "involved — including family — is shown the same method.",
      },
      {
        h: "Ceiling and wall fixing",
        p:
          "Ceiling track and grab rails must be fixed into adequate structure. Have fixings " +
          "specified and installed professionally rather than assumed.",
      },
      {
        h: "Professional guidance",
        p:
          "An occupational therapist or community nurse can assess transfers and recommend " +
          "specific equipment, which is strongly advisable before buying in this category.",
      },
    ],
  },

  {
    slug: "respiratory-emergency",
    name: "Respiratory & Emergency Equipment",
    nav: "Respiratory & Emergency Equipment",
    eyebrow: "Category 05",
    cardText:
      "Oxygen concentrators, respiratory support and home emergency equipment, explained plainly.",
    image: {
      base: "/media/medical-mobility/respiratory-emergency-equipment",
      alt:
        "A woman relaxing in an upscale living room beside a home oxygen concentrator, with an " +
        "AED cabinet on the sideboard behind her",
    },
    offerCategory: "respiratory-emergency",
    seo: {
      title: "Oxygen Concentrators & Home Emergency Equipment — Human Home Living Medical & Mobility",
      description:
        "Understand portable and stationary oxygen concentrators, respiratory support equipment " +
        "and AEDs for the home and workplace, including prescription and professional evaluation " +
        "requirements.",
    },
    lede:
      "This is the most tightly regulated category in this section, and the one where general " +
      "information is most useful and specific recommendations are least appropriate. We explain " +
      "how the equipment differs; a clinician decides what is right for a person.",
    overview: [
      "Oxygen equipment is prescribed. Flow rate, delivery method and whether a device delivers a " +
        "continuous flow or pulsed doses are clinical decisions, and a device that suits one " +
        "person's prescription can be unsuitable for another's.",
      "Emergency equipment follows a different logic: it is bought in advance, rarely used, and " +
        "has to work immediately when it is. Readiness — placement, battery and pad expiry, and " +
        "whether anyone present knows where it is — matters as much as the specification.",
    ],
    equipment: [
      {
        h: "Portable oxygen concentrators",
        p:
          "Battery-powered units for travel and time away from home. Most deliver pulse doses, " +
          "which is why a prescription has to state whether pulse delivery is acceptable.",
      },
      {
        h: "Stationary oxygen concentrators",
        p:
          "Mains-powered home units offering higher continuous flow capability, usually paired " +
          "with tubing that allows movement around the house.",
      },
      {
        h: "Respiratory support equipment",
        p:
          "Nebulisers, suction units, airway clearance devices and sleep-therapy equipment, " +
          "typically supplied and set up under clinical direction.",
      },
      {
        h: "AEDs",
        p:
          "Automated external defibrillators for homes, workplaces and community spaces, " +
          "including cabinets, pads and training units.",
      },
      {
        h: "Home and workplace emergency equipment",
        p:
          "First aid provision, emergency oxygen where permitted, and backup power considerations " +
          "for households that depend on powered medical equipment.",
      },
    ],
    considerations: [
      {
        h: "Prescription and evaluation",
        p:
          "Oxygen therapy and much respiratory equipment requires a prescription or professional " +
          "evaluation. Nothing in this section should be read as a recommendation that a device " +
          "suits any individual's medical condition.",
      },
      {
        h: "Flow and delivery",
        p:
          "Match the device to the prescribed flow rate and delivery type. Continuous flow and " +
          "pulse dose are not interchangeable.",
      },
      {
        h: "Battery life and power",
        p:
          "For portable units, check real battery duration at the prescribed setting, charging " +
          "time and spare battery options. For mains units, plan for power outages.",
      },
      {
        h: "Noise, weight and travel",
        p:
          "Weight, noise level and airline approval decide whether a portable unit genuinely gets " +
          "used away from home.",
      },
      {
        h: "Maintenance and consumables",
        p:
          "Filters, cannulas, tubing, AED pads and batteries all expire. Include replacement " +
          "intervals and costs in any comparison.",
      },
      {
        h: "Training and placement",
        p:
          "Emergency equipment only helps if it is reachable and someone is confident using it. " +
          "Plan placement and training at the same time as the purchase.",
      },
    ],
  },
];

export const mmCategory = (slug) => MM_CATEGORIES.find((c) => c.slug === slug);
export const mmPath = (slug) => `${MM_ROOT}/${slug}`;
